import { AddressValidationActions } from './enum';
import { call, put, takeLatest } from 'redux-saga/effects';
import { jarvisSaga, trackError } from './util/generator';
import { AddressFormId, AvailabilityResultId, CaptchaEvents } from '../../enum';
import { dispatchCaptchaEvent } from '../../components/captcha';
import {
    Address,
    deleteAddressCookie,
    getAddressCookie,
    getSessionAvcResult,
    setAvcResultSession,
    AvcResult,
    setAddressCookie,
} from '@vfde-sails/avc-core';
import { setLoading } from '../App/action';
import {
    setAddresses,
    setJarvisError,
    setJarvisResponse,
    checkAddress,
} from './action';
import { JarvisResponse } from '../../interface';
import packageJson from '../../../package.json';
import { trackCheckComplete, trackCheckOutcome, trackCheckStart, trackPageView } from '../../tracking/tracking';
import { debugInfo } from '../../util/debug';
import { scrollTo } from '../../util/scroll';
import { setCookie } from '@vfde-sails/utils';
import { ADDRESS_COOKIE, ST_COOKIE_DOMAIN } from '../../constants';

/**
 * availabilityCheckSaga
 * Implements the complete flow of AvailabilityCheck.
 */
export function* availabilityCheckSaga({ isSeamless }: ReturnType<typeof checkAddress>) {
    yield call(dispatchCaptchaEvent, CaptchaEvents.REJECTED);

    yield call(trackCheckStart, isSeamless);
    yield put(setLoading());

    try {
        const result: JarvisResponse = yield call(jarvisSaga);

        yield put(setJarvisResponse(result));

        if (!result.address) {
            return;
        }

        yield call(setAddressCookie, result.address);
        yield call(setAvcResultSession, result as AvcResult, packageJson.name);
        yield call(scrollTo, AvailabilityResultId.WRAPPER);
        yield call(trackCheckComplete, result.suggestion?.primary, result.suggestion?.secondary, isSeamless, result.availability);
        yield call(trackCheckOutcome, result.suggestion?.primary, result.suggestion?.secondary, isSeamless);
    } catch (error) {
        yield call(debugInfo, error);
        yield put(setJarvisError(error));
        yield call(trackError, error);
    } finally {
        yield put(setLoading(false));
    }
}

/**
 * Implements seamless availabilitycheck using address cookie and session storage
 */
export function* seamlessCheckSaga() {
    const address: Address | undefined = yield call(getAddressCookie);

    // If address cookie is not defined seamless check is not possible
    if (!address) {
        yield call(trackPageView);
        return;
    }

    yield put(setAddresses([address]));

    const result: JarvisResponse | undefined = yield call(getSessionAvcResult, packageJson.name, address);

    // If avcResult is valid return early
    if (result) {
        yield put(setJarvisResponse(result));
        yield call(trackCheckOutcome, result.suggestion?.primary, result.suggestion?.secondary, true);
        return;
    }

    // If address is defined but avcResult is not, then start availabilitycheck
    yield put(checkAddress(true));
}

export function* changeAddressSaga() {
    yield call(scrollTo, AddressFormId.WRAPPER);
}

/**
 * Handle address delete request
 */
export function* deleteAddressSaga() {
    yield call(deleteAddressCookie);
    yield call(scrollTo, AddressFormId.WRAPPER);
}

export default function* saga() {
    yield takeLatest(AddressValidationActions.CHECK_ADDRESS, availabilityCheckSaga);
    yield takeLatest(AddressValidationActions.CHANGE_ADDRESS, changeAddressSaga);
    yield takeLatest(AddressValidationActions.DELETE_ADDRESS, deleteAddressSaga);
}
